"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenSalzburg } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Salzburg() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Salzburg verkaufen: So funktioniert es am besten</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Salzburg.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Salzburg"
        link="/auto-verkaufen/salzburg"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/salzburg"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Du willst dein Auto in Salzburg verkaufen? Warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Ein Auto zu verkaufen ist nie eine alltägliche Entscheidung.
                    In Salzburg gibt es einige Besonderheiten, die den Verkauf
                    beeinflussen. Viele Menschen pendeln täglich nach Bayern
                    oder nach Oberösterreich. In den alpinen Regionen wie
                    Pinzgau oder Pongau sind Allradfahrzeuge besonders beliebt.
                    In der Stadt Salzburg hingegen fahren viele kleinere und
                    sparsame Autos. Gleichzeitig ist der Gebrauchtwagenmarkt
                    stark umkämpft. Dadurch wird es schwieriger, den richtigen
                    Käufer zu finden.
                  </p>
                  <p>
                    Wenn du dein Auto in Salzburg verkaufen möchtest, brauchst
                    du eine Lösung, die schnell, transparent und sicher
                    funktioniert. Genau hier hilft dir Autoeinfachlos.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Salzburg – worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Ein Auto zu verkaufen klingt einfach. Inserat erstellen,
                    Interessenten treffen, Preis verhandeln. In der Praxis sieht
                    es aber anders aus.
                  </p>
                  <p>
                    <strong>Regionale Besonderheiten:</strong> In Salzburg gibt
                    es unterschiedliche Anforderungen. Pendlerautos für den
                    täglichen Weg nach Bayern. Allradfahrzeuge für Bergregionen
                    wie den Tennengau oder Lungau. Kompakte Stadtautos für
                    Salzburg-Stadt.
                  </p>
                  <p>
                    <strong>Pendlerautos:</strong> Viele Salzburger fahren
                    regelmäßig lange Strecken. Das bedeutet hohe
                    Kilometerstände, auf die Käufer besonders achten.
                  </p>
                  <p>
                    <strong>Familienfahrzeuge:</strong> Vans, Kombis und SUVs
                    sind stark gefragt. Dennoch ist es schwer, sie privat zu
                    einem guten Preis zu verkaufen.
                  </p>
                  <p>
                    Privatverkauf bedeutet meistens Stress. Du musst Inserate
                    schalten, Termine vereinbaren und dich auf
                    Preisverhandlungen einstellen. Nicht selten bekommst du auch
                    unseriöse Angebote.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Salzburg</h3>
                  <p>
                    Mit Autoeinfachlos verkaufst du dein Auto in ganz Salzburg.
                    Egal ob in der Stadt Salzburg, Hallein, Zell am See, St.
                    Johann oder im Flachgau.
                  </p>
                  <p>Deine Vorteile:</p>
                  <ul className="custom-unordered-list">
                    <li>
                      Über 5.000 Händler bieten auf dein Auto, auch Händler aus
                      Salzburg
                    </li>
                    <li>
                      Du bekommst den besten Preis durch Auktion, nicht durch
                      harte Einzelverhandlungen
                    </li>
                    <li>Dein Auto wird direkt bei dir abgeholt</li>
                    <li>Sichere und schnelle Bezahlung</li>
                  </ul>
                  <p>So verkaufst du dein Auto einfach und ohne Risiko.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Salzburg - privat oder Händler?"
              rightSecondText={
                <>
                  <p>
                    Die Frage stellen sich viele: Soll ich mein Auto privat
                    verkaufen oder an einen Händler in Salzburg?
                  </p>
                  <p>
                    Privatverkauf: Klingt oft nach mehr Geld. In echt bedeutet
                    das viele Anfragen, ständige Besichtigungen und Unsicherheit
                    bei der Zahlung.
                  </p>
                  <p>
                    Direkter Händlerverkauf: Ist zwar schnell, aber ein
                    einzelner Händler zahlt selten den Marktpreis. Er will
                    schließlich selbst Gewinn machen.
                  </p>
                  <p>
                    Die Lösung ist Autoeinfachlos. Du profitierst vom großen
                    Händlernetzwerk und bekommst durch die Auktion einen fairen
                    Marktpreis. Du musst selbst nicht verhandeln.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Salzburg wert?"
                alt="Auto in Salzburg bewerten"
                text={
                  <>
                    <p>
                      Der Wert hängt von vielen Faktoren ab. Marke, Modell,
                      Baujahr, Laufleistung und Zustand. Auch die Nachfrage in
                      Salzburg spielt eine Rolle.
                    </p>
                    <p>Beispiele:</p>
                    <ul className="custom-unordered-list">
                      <li>Allradfahrzeuge sind im Gebirge besonders gefragt</li>
                      <li>
                        Pendlerautos mit vielen Kilometern haben einen
                        niedrigeren Marktwert
                      </li>
                      <li>
                        Kompakte Stadtflitzer sind in Salzburg-Stadt beliebt
                      </li>
                    </ul>
                    <p>
                      Bei Autoeinfachlos kannst du dein Auto kostenlos online
                      bewerten. So hast du sofort eine realistische
                      Einschätzung.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Salzburg</h3>
                    <p>Der Ablauf ist einfach.</p>
                    <ul className="custom-unordered-list">
                      <li>Gib dein Auto online zur Bewertung ein</li>
                      <li>Wir starten die Auktion im Händlernetzwerk</li>
                      <li>Du erhältst das höchste Gebot</li>
                      <li>Dein Auto wird direkt bei dir in Salzburg abgeholt</li>
                      <li>Du bekommst dein Geld sicher und schnell</li>
                    </ul>
                    <p>Schnell, fair und unkompliziert.</p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Autoverkauf in Salzburg"
              fragen={fragenSalzburg}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Salzburg"
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Salzburg mit Autoeinfachlos"
                alt="Auto verkaufen in Salzburg mit Autoeinfachlos"
                text={
                  <>
                    <p>
                      Privatverkauf kostet dich Zeit und Nerven. Mit
                      Autoeinfachlos verkaufst du dein Auto zum besten Preis.
                      Durch unser Händlernetzwerk, regionale Abholung und
                      sichere Bezahlung.
                    </p>
                    <p>Starte jetzt mit einer kostenlosen Bewertung:</p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Salzburg;
